import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { InboxApplication, InboxStats } from '@/types'
import { apiService } from '@/services/api.service'

export const useInboxStore = defineStore('inbox', () => {
  const applications = ref<InboxApplication[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const currentFilter = ref<'all' | 'pending' | 'in_progress' | 'completed'>('all')

  // Computed stats
  const stats = computed<InboxStats>(() => {
    const pending = applications.value.filter(app => app.task_status === 'pending').length
    const in_progress = applications.value.filter(app => app.task_status === 'in_progress').length
    const completed = applications.value.filter(app => app.task_status === 'completed').length
    
    return {
      pending,
      in_progress,
      completed,
      total: applications.value.length
    }
  })

  // Get filtered applications
  const filteredApplications = computed(() => {
    if (currentFilter.value === 'all') {
      return applications.value
    }
    return applications.value.filter(app => app.task_status === currentFilter.value)
  })

  // Group applications by position
  const applicationsByPosition = computed(() => {
    const grouped = new Map<string, { position_title: string; applications: InboxApplication[] }>()
    
    filteredApplications.value.forEach(app => {
      if (!grouped.has(app.position_id)) {
        grouped.set(app.position_id, {
          position_title: app.position_title,
          applications: []
        })
      }
      grouped.get(app.position_id)!.applications.push(app)
    })
    
    return grouped
  })

  async function fetchInbox(statusFilter?: string) {
    loading.value = true
    error.value = null

    try {
      const url = statusFilter && statusFilter !== 'all' ? `/inbox/?status_filter=${statusFilter}` : '/inbox/'
      const response = await apiService.get<{ success: boolean; applications: InboxApplication[]; total: number }>(url)
      
      if (response.data.success) {
        applications.value = response.data.applications
      }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Failed to fetch inbox'
      console.error('Error fetching inbox:', err)
      throw error.value
    } finally {
      loading.value = false
    }
  }

  async function updateTaskStatus(applicationId: string, status: 'pending' | 'in_progress' | 'completed') {
    try {
      await apiService.put(`/inbox/applications/${applicationId}/task-status`, { task_status: status })
      
      // Update local state
      const app = applications.value.find((a: InboxApplication) => a.id === applicationId)
      if (app) {
        app.task_status = status
      }
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Failed to update task status'
      throw error.value
    }
  }

  async function addStageComment(applicationId: string, stageName: string, comment: string) {
    try {
      await apiService.post(`/inbox/applications/${applicationId}/stage-comment`, {
        stage_name: stageName,
        comment: comment
      })
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Failed to add comment'
      throw error.value
    }
  }

  function setFilter(filter: 'all' | 'pending' | 'in_progress' | 'completed') {
    currentFilter.value = filter
  }

  function $reset() {
    applications.value = []
    loading.value = false
    error.value = null
    currentFilter.value = 'all'
  }

  return {
    applications,
    loading,
    error,
    currentFilter,
    stats,
    filteredApplications,
    applicationsByPosition,
    fetchInbox,
    updateTaskStatus,
    addStageComment,
    setFilter,
    $reset
  }
})

